import { SearchableConversationFilterInput } from 'generated-graphql/types';
import { useDebounce } from '@uidotdev/usehooks';
import { useState } from 'react';
import { getConversationFiltersFromFilterValue } from '../utils/getConversationFiltersFromFilterValue';
import { buildConversationSearchClauses } from '../utils/buildConversationSearchClauses';
import {
  buildConversationVisibilityClause,
  ConversationVisibility
} from '../utils/buildConversationVisibilityClause';
import {
  ConversationColumnFilter,
  ConversationTableFilter,
  ConversationTagFilter
} from '../types/ConversationTableFilter';

type Props = {
  organizationId: string;
  searchText: string;
  // Undefined for admins, who see the whole sub-account.
  visibility?: ConversationVisibility;
  // One entry per selected tag key, from useConversationTagFilters.
  tagFilters?: ConversationTagFilter[];
};

/**
 * Filter state for the cross-campaign conversations tab. The sibling hook
 * useConversationsFilters pins `campaignId`; this one pins `organizationId`,
 * which is what keeps sub-accounts apart, then narrows to what the user may see.
 */
export const useCrossCampaignConversationsFilters = ({
  organizationId,
  searchText,
  visibility,
  tagFilters = []
}: Props) => {
  const [conversationsTableFilters, setConversationsTableFilters] = useState<
    ConversationColumnFilter[] | undefined
  >(undefined);

  const debouncedSearchText = useDebounce(searchText, 500);

  const appliedFilters: ConversationTableFilter[] = [
    ...(conversationsTableFilters ?? []),
    ...tagFilters
  ];

  const andClauses = [
    ...buildConversationVisibilityClause(visibility),
    ...buildConversationSearchClauses({ searchText: debouncedSearchText }),
    ...(appliedFilters.length
      ? getConversationFiltersFromFilterValue(appliedFilters).and ?? []
      : [])
  ];

  const queryFilter: SearchableConversationFilterInput = {
    organizationId: {
      eq: organizationId
    },
    ...(andClauses.length > 0 ? { and: andClauses } : {})
  };

  // What Export data sends. Undefined unless a search or filter is applied, so
  // an export with nothing applied stays the whole-account export it always
  // was. Keyed off the debounced text, like queryFilter, so the file matches
  // the rows the list actually loaded. Not `queryFilter` being empty: it
  // always pins the organization, and a non-admin's visibility clause.
  const exportFilter =
    appliedFilters.length > 0 || debouncedSearchText.trim()
      ? queryFilter
      : undefined;

  return {
    queryFilter,
    exportFilter,
    isFiltering: !!appliedFilters.length || !!searchText.trim(),
    conversationsTableFilters,
    setConversationsTableFilters,
    appliedFilters
  };
};
